import _ from "@/boot/lodash";
import type { IFilter } from "@/models/tables";
import { FilterOperators, FilterTypes, ApiOperators } from "@/data/table";
import { UUID } from "@/data/enums";
import {
  ContractStatusCodes as CSC,
  ContractStatusMap
} from "@/models/contracts";
import i18n from "@/i18n";
import store from "@/store";
import { DataModules } from "@/store/modules/data/modules";
import type { IProduct } from "@/models/products";
import type { TranslateResult } from "vue-i18n";
import type { IBrand } from "@upmind-automation/types";

// Params

export const IsActiveParam = () => ({
  "filter[status.code]": [
    CSC.ACTIVE,
    CSC.AWAITING_ACTIVATION,
    CSC.PENDING,
    CSC.SUSPENDED
  ].join()
});

export const statusParam = (statuses: CSC[]) => ({
  "filter[status.code]": statuses.join()
});

export const isFromLast30DaysParam = () => ({
  [`filter[created_at|${ApiOperators.AFTER}]`]: "-1_months"
});

export const hasUnresolvedRequestsParam = () => ({
  [`filter[unresolved_provision_requests.id|${ApiOperators.NOT_EQUAL}]`]: "null"
});

export const IsInactiveParam = () => ({
  "filter[status.code]": [CSC.CANCELLED, CSC.CLOSED].join()
});

export const IsSubscriptionParam = () => ({
  [`filter[billing_cycle_days|${ApiOperators.NOT_EQUAL}]`]: 0
});

export const IsOneTimePurchaseParam = () => ({
  "filter[billing_cycle_days]": 0
});

export const IsCategoryParam = vm => ({
  "filter[product.category.id]": vm?.$route?.params?.categoryId
});

export const ClientsIdParam = ({ clientId }) => ({
  "filter[clients.id]": clientId
});

export const CatalogueProductIdParam = (productId: IProduct["id"]) => ({
  "filter[product.id]": productId
});

// Filters

export const ProductNameFilter: () => IFilter = () => ({
  key: "product.name",
  label: i18n.t("_.product_name"),
  type: FilterTypes.STRING,
  operator: FilterOperators.CONTAINS,
  group: "product"
});

export const ProductIdFilter: () => IFilter = () => ({
  key: "product_id",
  label: i18n.t("_.product_id"),
  type: FilterTypes.STRING_EQUAL,
  operator: FilterOperators.EQUAL,
  group: "product"
});

export const StatusCodesFilter: (statusCodes?: CSC[]) => IFilter = (
  statusCodes = _.values(CSC)
) => ({
  key: "status.code",
  label: i18n.t("_.status"),
  type: FilterTypes.SELECT_CHECKBOX,
  options: _.map(
    _.pick(ContractStatusMap(), statusCodes),
    ({ name }, value) => ({
      label: name,
      value
    })
  ),
  operator: FilterOperators.EQUAL,
  group: "product"
});

export const ServiceIdentifierFilter: () => IFilter = () => ({
  key: "service_identifier",
  label: i18n.t("_.service_identifier"),
  type: FilterTypes.STRING,
  operator: FilterOperators.CONTAINS,
  group: "subscription",
  if: () => store.getters.isAdminContext
});

export const ImportIdFilter: () => IFilter = () => ({
  key: "import_id",
  label: i18n.t("_.import_id"),
  type: FilterTypes.STRING,
  operator: FilterOperators.EQUAL,
  group: "subscription",
  if: () => store.getters.isAdminContext
});

export const TermFilter: () => IFilter = () => ({
  key: "billing_cycle_months",
  label: i18n.t("_.billing_cycle_months"),
  type: FilterTypes.NUMBER,
  operator: FilterOperators.EQUAL,
  group: "subscription",
  if: () => store.getters.isAdminContext
});

export const ProvisionBluePrintId: () => IFilter = () => {
  const getOptions = async () => {
    const blueprints = await store.dispatch(
      `data/${DataModules.PROVISIONING_BLUEPRINTS}/list`,
      {
        storeData: false,
        returnData: true,
        params: {}
      }
    );

    return mapSortByOrder(blueprints);
  };

  return {
    key: "product.provision_blueprint_id",
    label: i18n.t("_.provision_blueprint"),
    type: FilterTypes.SELECT_CHECKBOX,
    operator: FilterOperators.EQUAL,
    group: "subscription",
    value: [],
    optionsFunc: getOptions,
    if: () => store.getters.isAdminContext
  } as IFilter;
};

export const ProductCategoryNameFilter: () => IFilter = () => ({
  key: "product.category.name",
  label: i18n.t("_.product_category"),
  type: FilterTypes.STRING,
  operator: FilterOperators.CONTAINS,
  if: () => store.getters.isClientContext,
  group: "product"
});

export const ClientIdFilter: () => IFilter = () => ({
  key: "client_id",
  label: i18n.t("_.client_id"),
  type: FilterTypes.STRING,
  operator: FilterOperators.EQUAL,
  isHidden: true,
  group: "client"
});

const brandMapData = data => {
  const brands = _.filter(
    store.state.auth.admin.brands,
    ({ id }) => id !== UUID.ORG
  );

  return _.filter(
    _.sortBy(
      _.map(_.uniqBy(data, "brand_id"), brandedData => {
        return {
          label: _.find(brands, ["id", brandedData.brand_id])?.name,
          id: `$brand_${brandedData.brand_id}`,
          isDisabled: true,
          children: mapSortByOrder(
            _.filter(data, ["brand_id", brandedData.brand_id])
          )
        };
      }),
      mappedData => mappedData.label?.toString().toLowerCase()
    ),
    mappedBrand => !!mappedBrand.children.length
  );
};

const mapSortByOrder = data => {
  return _.map(_.orderBy(data, ["order", "asc"]), item => ({
    id: item.id,
    value: item.id,
    label: item.name,
    ...((item.subcategories || []).length
      ? { children: mapSortByOrder(item.subcategories) }
      : {})
  }));
};

export const ProductCategoryFilter: (
  brandId?: IBrand["id"] | null,
  config?: {
    key?: string;
    label?: TranslateResult;
    group?: string;
    if?: () => boolean;
  }
) => IFilter = (
  brandId = null,
  config = {
    key: "product.category.id",
    label: i18n.t("_.product_category"),
    group: "product",
    if: () => true
  }
) => {
  const isMultibrand = store.getters["brand/isMultibrand"];

  const getOptions = async () => {
    const categories = await store.dispatch(
      `data/${DataModules.CATALOGUE_CATEGORIES}/list`,
      {
        storeData: false,
        params: {
          order: "order",
          limit: 0,
          "filter[level]": 1,
          ...(brandId && brandId !== store.getters["brand/id"]
            ? { "filter[brand_id]": brandId }
            : {}),
          brandId: brandId ? brandId : store.getters["brand/id"]
        }
      }
    );

    return isMultibrand && !brandId
      ? brandMapData(categories)
      : mapSortByOrder(categories);
  };

  const filter: IFilter = {
    key: config.key,
    label: config.label,
    type: FilterTypes.SELECT_MULTI,
    value: [],
    operator: FilterOperators.EQUAL,
    optionsFunc: getOptions,
    isBranded: true,
    group: config.group,
    if: config.if
  };

  return filter;
};

export const BrandFilter: () => IFilter = () => {
  const getOptions = () =>
    store.state?.auth?.admin?.brands?.map(brand => ({
      id: brand.id,
      label: brand.name
    }));

  const filter: IFilter = {
    key: "product.brand_id",
    label: i18n.t("_.brand"),
    type: FilterTypes.SELECT_MULTI,
    value: [],
    operator: FilterOperators.EQUAL,
    group: "product",
    optionsFunc: getOptions,
    if: () => store.getters["brand/isMultibrand"]
  };

  return filter;
};

export const ActiveStatusCodesFilter = () =>
  StatusCodesFilter([
    CSC.ACTIVE,
    CSC.AWAITING_ACTIVATION,
    CSC.PENDING,
    CSC.SUSPENDED
  ]);

export const AllStatusCodesFilter = () =>
  StatusCodesFilter([
    CSC.ACTIVE,
    CSC.AWAITING_ACTIVATION,
    CSC.PENDING,
    CSC.SUSPENDED,
    CSC.CANCELLED,
    CSC.CLOSED,
    CSC.FRAUD
  ]);

export const ProvisionProviderId: (
  filters?: Record<string, string>
) => IFilter = filters => {
  const getOptions = async () => {
    const configurations = await store.dispatch(
      `data/${DataModules.PROVISIONING_CATEGORIES}/list`,
      {
        storeData: false,
        returnData: true,
        params: {
          limit: 0,
          with: "providers",
          ...(typeof filters === "object" ? filters : {})
        }
      }
    );

    return configurations.map(i => ({
      value: i.id,
      label: i.name,
      name: i.name,
      options: i.providers.map(provider => ({
        value: provider.id,
        label: provider.name
      }))
    }));
  };

  const filter: IFilter = {
    key: "provision_provider_id",
    label: i18n.t("_.provision_provider"),
    type: FilterTypes.SELECT,
    operator: FilterOperators.EQUAL,
    optionsFunc: getOptions,
    group: "subscription",
    if: () => store.getters.isAdminContext
  };
  return filter;
};

export const HasProvisionIssuesFilter = () => {
  return {
    key: "unresolved_provision_requests.id",
    label: i18n.t("_.with_provision_issues_only"),
    options: [{ value: "null", label: i18n.t("_.yes") }],
    type: FilterTypes.CUSTOM_NEGATIVE,
    operator: FilterOperators.NOT_EQUAL,
    group: "subscription",
    value: "null",
    isLocked: true,
    if: () => store.getters.isAdminContext
  };
};

export const IsSubscriptionFilter = () => {
  return {
    key: "billing_cycle_days",
    label: i18n.t("_.subscriptions_only"),
    options: [{ value: "0", label: i18n.t("_.yes") }],
    type: FilterTypes.CUSTOM_NEGATIVE,
    operator: FilterOperators.NOT_EQUAL,
    group: "subscription",
    isLocked: true,
    value: "0"
  };
};

export const IsOneTimeFilter = () => {
  return {
    key: "billing_cycle_days",
    label: i18n.t("_.one_time_only"),
    options: [{ value: "0", label: i18n.t("_.yes") }],
    type: FilterTypes.CUSTOM_NEGATIVE,
    operator: FilterOperators.EQUAL,
    group: "subscription",
    isLocked: true,
    value: "0"
  };
};

export const QuickSearchFilter: () => IFilter = () => ({
  key: "query",
  label: i18n.t("_.quick_search"),
  type: FilterTypes.CUSTOM_NEGATIVE,
  operator: FilterOperators.CONTAINS,
  group: "subscription"
});

export const ProvisionConfigurationId: (
  filters?: Record<string, string>,
  brandId?: IBrand["id"] | null
) => IFilter = (filters, brandId = null) => {
  const isMultibrand = store.getters["brand/isMultibrand"];

  const getOptions = async () => {
    const configurations = await store.dispatch(
      `data/${DataModules.PROVISIONING_CONFIGURATIONS}/list`,
      {
        storeData: false,
        returnData: true,
        params: {
          limit: 0,
          ...(typeof filters === "object" ? filters : {})
        }
      }
    );

    return isMultibrand && !brandId
      ? brandMapData(configurations)
      : mapSortByOrder(
          brandId
            ? _.filter(configurations, ["brand_id", brandId])
            : configurations
        );
  };

  const filter: IFilter = {
    key: "provision_configuration_id",
    label: i18n.t("_.provision_configuration"),
    type: FilterTypes.SELECT_MULTI,
    operator: FilterOperators.EQUAL,
    group: "subscription",
    value: [],
    isBranded: true,
    optionsFunc: getOptions,
    if: () => store.getters.isAdminContext
  };
  return filter;
};

export const CancelledStatusCodesFilter = () =>
  StatusCodesFilter([CSC.CLOSED, CSC.CANCELLED]);

export const CurrencyCodeFilter: () => IFilter = () => ({
  key: "currency_base.code",
  label: i18n.t("_.currency_code"),
  type: FilterTypes.STRING,
  operator: FilterOperators.EQUAL,
  group: "product"
});

export const PriceFilter: () => IFilter = () => ({
  key: "total_amount",
  label: i18n.t("_.price"),
  type: FilterTypes.NUMBER,
  operator: FilterOperators.EQUAL,
  group: "product"
});

export const DatePurchasedFilter: () => IFilter = () => ({
  key: "created_at",
  label: i18n.t("_.date_purchased"),
  type: FilterTypes.DATE,
  operator: FilterOperators.AFTER,
  group: "product"
});

export const NextDueDateFilter: () => IFilter = () => ({
  key: "next_due_date",
  label: i18n.t("_.next_due_date"),
  type: FilterTypes.DATE,
  operator: FilterOperators.AFTER,
  group: "product"
});

export const CProdClientsFilters: () => IFilter[] = () => [
  {
    key: "clients.username",
    label: i18n.t("_.client_username"),
    type: FilterTypes.STRING,
    operator: FilterOperators.CONTAINS,
    group: "client"
  },
  {
    key: "clients.has_login",
    label: i18n.t("_.client_has_login"),
    type: FilterTypes.SELECT_RADIO,
    operator: FilterOperators.EQUAL,
    value: "1",
    group: "client"
  },
  {
    key: "clients.firstname",
    label: i18n.t("_.client_firstname"),
    type: FilterTypes.STRING,
    operator: FilterOperators.CONTAINS,
    group: "client"
  },
  {
    key: "clients.lastname",
    label: i18n.t("_.client_lastname"),
    type: FilterTypes.STRING,
    operator: FilterOperators.CONTAINS,
    group: "client"
  },
  {
    key: "clients.created_at",
    label: i18n.t("_.client_created"),
    type: FilterTypes.DATE,
    operator: FilterOperators.AFTER,
    group: "client"
  },
  {
    key: "clients.id",
    label: i18n.t("_.client_id"),
    type: FilterTypes.STRING,
    operator: FilterOperators.EQUAL,
    group: "client"
  },
  {
    key: "clients.verified",
    label: i18n.t("_.client_verified"),
    type: FilterTypes.SELECT_RADIO,
    operator: FilterOperators.EQUAL,
    value: "1",
    group: "client"
  }
];

export const CProdProvisionFilters: () => IFilter[] = () => [
  {
    key: "product.provision_blueprint_id",
    label: i18n.t("_.provision_blueprint"),
    type: FilterTypes.SELECT_CHECKBOX,
    operator: FilterOperators.EQUAL,
    group: "subscription",
    value: []
  },
  {
    key: "provision_provider_id",
    label: i18n.t("_.provision_provider"),
    type: FilterTypes.SELECT,
    operator: FilterOperators.EQUAL,
    group: "subscription"
  },
  {
    key: "provision_configuration_id",
    label: i18n.t("_.provision_configuration"),
    type: FilterTypes.SELECT_CHECKBOX,
    operator: FilterOperators.EQUAL,
    group: "subscription",
    value: []
  }
];

export const DefaultCProdFilters: (
  lockedFilters?: IFilter["key"][],
  brandId?: IBrand["id"] | null
) => IFilter[] = (lockedFilters = [], brandId = null) => {
  return _.map(
    [
      BrandFilter(),
      DatePurchasedFilter(),
      NextDueDateFilter(),
      CurrencyCodeFilter(),
      PriceFilter(),
      ProductNameFilter(),
      ProductIdFilter(),
      ServiceIdentifierFilter(),
      ProductCategoryFilter(brandId),
      ProductCategoryNameFilter(),
      TermFilter(),
      AllStatusCodesFilter(),
      ImportIdFilter(),
      ProvisionBluePrintId(),
      ProvisionProviderId(),
      ProvisionConfigurationId({}, brandId)
    ],
    filter => {
      if (Array.isArray(lockedFilters) && lockedFilters.includes(filter.key))
        filter.isLocked = true;
      return filter;
    }
  );
};

export const RetentionsFilters: (brandId?: IBrand["id"] | null) => IFilter[] = (
  brandId = null
) => {
  return [
    BrandFilter(),
    DatePurchasedFilter(),
    NextDueDateFilter(),
    CurrencyCodeFilter(),
    PriceFilter(),
    ProductNameFilter(),
    ProductCategoryFilter(brandId),
    ServiceIdentifierFilter(),
    ImportIdFilter()
  ];
};

export const CatalogueProductCProdFilters: () => IFilter[] = () => {
  return [
    DatePurchasedFilter(),
    NextDueDateFilter(),
    CurrencyCodeFilter(),
    PriceFilter(),
    ServiceIdentifierFilter(),
    ImportIdFilter()
  ];
};
